import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * SharedFileViewer — public file access via share token.
 * Route: /s/:token
 * No login required. Handles expired links, password-protected files.
 */
const SharedFileViewer = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadFile = async (pwd) => {
    try {
      const url = `/api/pipelines/shared/${token}${pwd ? `?p=${encodeURIComponent(pwd)}` : ''}`;
      const res = await fetch(url);

      if (res.status === 401) {
        const data = await res.json();
        if (data.error === 'password_required' || data.error === 'wrong_password') {
          setNeedsPassword(true);
          if (data.error === 'wrong_password') setError('Incorrect password');
          setStatus('password');
          return;
        }
      }

      if (res.status === 410) {
        setError('This link has expired.');
        setStatus('error');
        return;
      }

      if (res.status === 404) {
        setError('This link is no longer valid.');
        setStatus('error');
        return;
      }

      if (!res.ok) {
        setError('Unable to load file.');
        setStatus('error');
        return;
      }

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.location.replace(blobUrl);
    } catch (err) {
      setError('Unable to load file.');
      setStatus('error');
    }
  };

  useEffect(() => { loadFile(); }, [token]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    setError('');
    await loadFile(password);
    setSubmitting(false);
  };

  if (status === 'password') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg border p-6 w-full max-w-sm">
          <div className="text-center mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            </div>
            <h2 className="text-sm font-semibold text-gray-900">Password Protected</h2>
            <p className="text-xs text-gray-500 mt-1">Enter the password to view this file</p>
          </div>
          {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600 text-center">{error}</div>}
          <form onSubmit={handlePasswordSubmit}>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus
              placeholder="Enter password" className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 mb-3" />
            <button type="submit" disabled={submitting || !password.trim()}
              className="w-full py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
              {submitting ? 'Verifying...' : 'View File'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg border p-6 max-w-sm text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          </div>
          <p className="text-sm text-gray-700 font-medium">{error}</p>
          <p className="text-xs text-gray-400 mt-2">Contact the person who shared this link for access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-3 text-sm text-gray-500">Opening file...</p>
      </div>
    </div>
  );
};

export default SharedFileViewer;
